/**
 * Cliente HTTP central. Lee tokens del store. Auto-refresca al recibir 401.
 */
import { useAuth } from "../auth-store";

const BASE = import.meta.env.VITE_API_URL ?? "/api";

type Body = unknown;

interface Opts {
    method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
    body?: Body;
    noAuth?: boolean;
}

export class ApiError extends Error {
    constructor(public status: number, message: string, public data?: unknown) {
        super(message);
    }
}

async function request<T = unknown>(path: string, opts: Opts = {}, retried = false): Promise<T> {
    const { method = "GET", body, noAuth = false } = opts;
    const headers: Record<string, string> = {};
    if (body !== undefined) headers["Content-Type"] = "application/json";
    if (!noAuth) {
        const token = useAuth.getState().accessToken;
        if (token) headers["Authorization"] = `Bearer ${token}`;
    }

    const res = await fetch(`${BASE}${path}`, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (res.status === 401 && !noAuth) {
        // Detectar kick por single-session ANTES de intentar refresh
        let kickedData: any = null;
        try { kickedData = await res.clone().json(); } catch {}
        if (kickedData?.error === "SESSION_KICKED") {
            // Marcar el motivo en sessionStorage para que el login muestre modal
            try {
                sessionStorage.setItem(
                    "certipol:kicked",
                    kickedData.message ?? "Tu sesión fue cerrada porque iniciaste sesión en otro dispositivo.",
                );
            } catch {}
            useAuth.getState().logout();
            throw new ApiError(401, kickedData.message ?? "SESSION_KICKED", kickedData);
        }
        if (!retried) {
            const refreshed = await useAuth.getState().refresh();
            if (refreshed) return request<T>(path, opts, true);
        }
        useAuth.getState().logout();
        throw new ApiError(401, "No autenticado");
    }

    if (!res.ok) {
        let data: any = null;
        try { data = await res.json(); } catch {}
        throw new ApiError(res.status, data?.error ?? res.statusText, data);
    }

    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
}

export const api = {
    get:    <T>(p: string) => request<T>(p),
    post:   <T>(p: string, body?: Body) => request<T>(p, { method: "POST", body }),
    patch:  <T>(p: string, body?: Body) => request<T>(p, { method: "PATCH", body }),
    delete: <T>(p: string) => request<T>(p, { method: "DELETE" }),
    raw: request,
    public: <T>(p: string, opts: Opts = {}) => request<T>(p, { ...opts, noAuth: true }),
};

/**
 * Subida directa a S3 usando una URL pre-firmada del backend.
 */
export async function uploadToS3(presignedUrl: string, file: File): Promise<void> {
    const res = await fetch(presignedUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
    });
    if (!res.ok) throw new Error(`Error subiendo a S3: ${res.status}`);
}
